import { HealthSystemDomainDeviceResultReaderViewModel } from '../api/HealthSystemApi';

export type ReaderStatus = 'healthy' | 'stale' | 'down';

export function getReaderStatus(reader: HealthSystemDomainDeviceResultReaderViewModel): ReaderStatus {
  const { lastHeartbeatAt, heartbeatIntervalSeconds, isStaleAlertSent } = reader;
  if (!lastHeartbeatAt) return 'down';
  const elapsed = Date.now() - new Date(lastHeartbeatAt).getTime();
  const intervalMs = heartbeatIntervalSeconds * 1000;
  if (elapsed <= intervalMs * 2) return 'healthy';
  if (isStaleAlertSent || elapsed <= intervalMs * 10) return 'stale';
  return 'down';
}

/**
 * Age-based thresholds, the rule the estate-wide sensor widget reads by: a device heard from inside a day is
 * healthy, one silent for up to a month is stale, anything older is down.
 *
 * This is deliberately separate from `getReaderStatus`, which judges a device against its own heartbeat
 * interval. That rule is right on a single slaughterhouse's device page, where a 60-second reader missing ten
 * beats is news; it is too twitchy for an estate roll-up, where a reader that reports twice a day would flap
 * between healthy and down all week. The backend owns these windows, so when it starts returning the status
 * on the view model both of these become a fallback and this is the one to replace first.
 */
export const READER_HEALTHY_MAX_HOURS = 24;
export const READER_STALE_MAX_DAYS = 30;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export function getReaderStatusByAge(lastHeartbeatAt?: string | null): ReaderStatus {
  // Never heard from is down, not stale: there is no heartbeat to have gone quiet.
  if (!lastHeartbeatAt) return 'down';
  const elapsed = Date.now() - new Date(lastHeartbeatAt).getTime();
  if (elapsed <= READER_HEALTHY_MAX_HOURS * HOUR_MS) return 'healthy';
  if (elapsed <= READER_STALE_MAX_DAYS * DAY_MS) return 'stale';
  return 'down';
}

/** Stale and down are both "someone should look at this"; healthy is not. */
export function needsAttention(status: ReaderStatus): boolean {
  return status !== 'healthy';
}

export function formatLastSeen(lastHeartbeatAt?: string | null): string {
  if (!lastHeartbeatAt) return '—';
  const diff = Math.floor((Date.now() - new Date(lastHeartbeatAt).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
